/**
 * Shop API base URL.
 * On custom domains prefer same-origin `/api` (Plesk proxies Host → shop) so auth
 * cookies stay first-party. apiOrigin/{slug}/api is cross-site and Chrome drops it.
 */
export function resolveShopApiBase(
  shopSlug: string | undefined | null,
  options: { apiOrigin?: string, platformApiUrl?: string, sameOriginApi?: boolean } = {},
): string {
  const slug = (shopSlug || '').trim().toLowerCase()
  if (!slug) {
    return String(options.platformApiUrl || '/api/platform')
  }

  if (options.sameOriginApi) {
    return '/api'
  }

  const origin = String(options.apiOrigin || '').replace(/\/$/, '')
  if (origin && /^https?:\/\//i.test(origin)) {
    return `${origin}/${slug}/api`
  }

  return `/${slug}/api`
}

/** Public API first, then the backend container when SSR cannot reach `*.localhost`. */
export function shopApiBases(
  shopSlug: string | undefined | null,
  options: { apiOrigin?: string, platformApiUrl?: string, sameOriginApi?: boolean, server?: boolean } = {},
): string[] {
  const primary = resolveShopApiBase(shopSlug, options)
  if (!options.server) return [primary]
  const internalOrigin = devDockerApiOrigin(String(options.apiOrigin || ''))
  if (!internalOrigin) return [primary]
  const internal = resolveShopApiBase(shopSlug, {
    apiOrigin: internalOrigin,
    platformApiUrl: options.platformApiUrl,
  })
  return internal && internal !== primary ? [primary, internal] : [primary]
}

/**
 * Dev containers resolve `*.localhost` to their own loopback, so Traefik on the
 * host is unreachable during SSR. The backend container is on the same network.
 * Bare `localhost` stays unchanged so host `npm run dev` still hits the API directly.
 */
export function devDockerApiOrigin(publicOrigin: string): string | null {
  try {
    const host = new URL(publicOrigin).hostname
    if (!host.endsWith('.localhost')) return null
  } catch {
    return null
  }
  return 'http://novartshop-backend:3001'
}
